// EmojiPicker.tsx - Simple emoji picker popup for desktop
import React, { useState, useRef, useEffect } from 'react';
import '../assets/styles/EmojiPicker.css';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  disabled?: boolean;
}

// Commonly used emojis organized by category
const EMOJI_CATEGORIES = {
  'Smileys': ['😊', '😂', '🥰', '😍', '🤔', '😅', '😭', '🥺', '😎', '🤗', '😏', '🙄', '😴', '🤯', '🥳', '😇'],
  'Gestures': ['👍', '👎', '👏', '🙏', '💪', '🤝', '✌️', '🤞', '👋', '🫡', '🤷', '🙌', '👀', '🫶', '✨', '💫'],
  'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💕', '💖', '💗', '💝', '❣️', '💘', '💓', '💞'],
  'Objects': ['🎉', '🎊', '🔥', '💡', '📝', '💻', '🎵', '🎨', '📚', '☕', '🍕', '🌟', '⭐', '🌈', '🚀', '💎'],
  'Nature': ['🌸', '🌺', '🌻', '🌹', '🌷', '🍀', '🌿', '🌴', '🌙', '☀️', '⛅', '🌊', '🦋', '🐶', '🐱', '🦊'],
};

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Smileys');
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
    // Keep picker open for multiple selections
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="emoji-picker-container">
      <button
        ref={buttonRef}
        className={`emoji-picker-button ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        title="Add emoji"
        type="button"
        aria-label="Open emoji picker"
        aria-expanded={isOpen}
      >
        😊
      </button>

      {isOpen && (
        <div ref={pickerRef} className="emoji-picker-popup">
          {/* Category tabs */}
          <div className="emoji-category-tabs">
            {Object.keys(EMOJI_CATEGORIES).map((category) => (
              <button
                key={category}
                className={`emoji-category-tab ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
                title={category}
                type="button"
              >
                {EMOJI_CATEGORIES[category as keyof typeof EMOJI_CATEGORIES][0]}
              </button>
            ))}
          </div>

          {/* Emoji grid */}
          <div className="emoji-grid">
            {EMOJI_CATEGORIES[activeCategory as keyof typeof EMOJI_CATEGORIES].map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                className="emoji-item"
                onClick={() => handleEmojiClick(emoji)}
                title={emoji}
                type="button"
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* Category label */}
          <div className="emoji-category-label">{activeCategory}</div>
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
